// 시안 v6 API(백엔드 v2) 클라이언트. 계약: backend docs/frontend-admin-v6-api-handoff.md(2026-10-12)
// 신원은 직원 토큰(Authorization: Bearer) — v6common/auth.js 가 보관한다.
import axios from "axios";
import { getToken, clearAuth, emitAuth } from "../v6common/auth";

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL });

export function withAuthHeader(config) {
    const t = getToken();
    if (t) config.headers = { ...(config.headers || {}), Authorization: `Bearer ${t}` };
    return config;
}
api.interceptors.request.use(withAuthHeader);

// 신원이 깨진 응답(계약 §1 표): 로그인으로 돌릴 것 · 비밀번호 변경 화면으로 보낼 것 · 사용 중지.
// 401 invalid_credentials 는 로그인 창 자신의 오류라 null.
export function authFailure(e) {
    const st = e?.response?.status, code = e?.response?.data?.detail?.code;
    if (st === 401 && (code === "token_required" || code === "invalid_token")) return "login";
    if (st === 403 && code === "password_change_required") return "password";
    if (st === 403 && code === "account_disabled") return "disabled";
    return null;
}
// 늦게 온 응답이 그 사이 새로 받은 토큰을 지우지 않게: 이 요청이 보낸 토큰이 지금 토큰일 때만 처리한다
export function handleAuthError(e) {
    const kind = authFailure(e);
    const sent = e?.config?.headers?.Authorization;
    const current = getToken();
    if (kind && (!sent || !current || sent === `Bearer ${current}`)) {
        if (kind === "login" || kind === "disabled") { clearAuth(); emitAuth(kind); }
        else if (kind === "password") emitAuth(kind);
    }
    return Promise.reject(e);
}
api.interceptors.response.use((r) => r, handleAuthError);

export function errorText(e) {
    const d = e?.response?.data?.detail;
    if (d && typeof d === "object" && !Array.isArray(d) && d.message) return d.message;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return "입력 형식을 확인해 주세요.";
    return "처리 중 오류가 발생했습니다.";
}
export const staleCurrent = (e) =>
    (e?.response?.status === 409 && e.response.data?.detail?.code === "stale_version" ? e.response.data.detail.current || null : null);

export const data = (p) => p.then((r) => r.data);
// 다른 관리자 메뉴(대시보드·공지·지식베이스 등)도 같은 클라이언트를 쓴다 — 토큰이 붙는다
export const http = api;

export const getMeta = () => data(api.get("/inquiries/meta"));
export const getStaffOptions = () => data(api.get("/inquiries/staff-options"));
export const getSummary = () => data(api.get("/inquiries/summary"));
export const getList = (params) => data(api.get("/inquiries/list", { params }));
export function getBoard(params, team) {
    const p = new URLSearchParams(params);
    if (team && team !== "전체") p.set("team", team);
    return data(api.get("/inquiries/board", { params: p }));
}
export const getDetail = (id) => data(api.get(`/inquiries/${id}/detail`));
export async function getConversation(id) {
    try { return await data(api.get(`/inquiries/${id}/conversation`)); }
    catch (e) { if (e?.response?.status === 404) return null; throw e; }
}
export const lookupStore = (biz) => data(api.get("/inquiries/lookup-store", { params: { business_number: String(biz).replace(/\D/g, "") } }));
export const exportCsv = (params) => data(api.get("/inquiries/export", { params, responseType: "blob" }));
export const markRead = (id) => api.post(`/inquiries/${id}/read`);

export const editInquiry = (id, body) => data(api.patch(`/inquiries/${id}/edit`, body));
// reason(보류·취소 사유)은 줬을 때만 보낸다 — 안 보내면 서버가 그대로 둔다(상태가 바뀔 때는 이번 reason 이 새 사유)
export const setStatus = (id, version, status, processContent, reason) =>
    data(api.post(`/inquiries/${id}/set-status`, {
        version, status,
        ...(processContent !== undefined ? { process_content: processContent } : {}),
        ...(reason !== undefined ? { reason } : {}),
    }));
export const addAssignee = (id, adminId) => data(api.post(`/inquiries/${id}/assignees`, { admin_id: adminId }));
export const removeAssignee = (id, adminId) => data(api.delete(`/inquiries/${id}/assignees/${adminId}`));
export const transferAssignee = (id, from, to) => data(api.post(`/inquiries/${id}/assignees/transfer`, { from_admin_id: from, to_admin_id: to }));
export const assignMe = (id) => data(api.post(`/inquiries/${id}/assignees/me`));
// captions 는 files 와 같은 수·순서(장당 50자). 전부 비면 안 보낸 것과 같아서 붙이지 않는다.
export function addAttachments(id, files, captions = []) {
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    if (captions.some((c) => (c || "").trim())) files.forEach((_, i) => form.append("captions", captions[i] || ""));
    return data(api.post(`/inquiries/${id}/attachments`, form));
}
export const createPhone = (body) => data(api.post("/inquiries/phone", body));

// 알림 종 두 번째 칸 「가맹점주 새 글」(§4-2)
export const getNewPosts = () => data(api.get("/inquiries/list", { params: { customer_new_post: true, status: "all", size: 20 } }));

// 직원 계정(§6, 대표)
export const listStaff = (q) => data(api.get("/admin/staff", { params: q ? { q } : {} }));
export const createStaff = (body) => data(api.post("/admin/staff", body));
export const resetStaffPassword = (id) => data(api.post(`/admin/staff/${id}/reset-password`));
export const disableStaff = (id) => data(api.post(`/admin/staff/${id}/disable`));
export const enableStaff = (id) => data(api.post(`/admin/staff/${id}/enable`));
export const deleteStaff = (id) => data(api.delete(`/admin/staff/${id}`));
export const getJoinCode = () => data(api.get("/admin/staff/join-code"));
export const rotateJoinCode = () => data(api.post("/admin/staff/join-code/rotate"));

export const fileUrl = (url) => (/^https?:\/\//.test(url || "") ? url : `${process.env.REACT_APP_API_URL}${url || ""}`);
